import { motion } from 'framer-motion'
import { CheckCircle, Shield, Clock, Award, Users, Zap } from 'lucide-react'
import { WHY_CHOOSE_US_STATS } from '../constants/stats'

const WhyChooseUs = () => {
  const advantages = [
    {
      icon: Shield,
      title: 'Гарантия качества',
      description: 'Предоставляем гарантию на все виды работ от 1 до 5 лет. Если возникнут проблемы - исправим бесплатно.',
      features: ['Письменная гарантия', 'Бесплатное исправление', 'Техническая поддержка']
    },
    {
      icon: Clock,
      title: 'Быстрое выполнение',
      description: 'Полная обработка автомобиля за 1-2 дня в зависимости от размера. Работаем без выходных и праздников.',
      features: ['Быстрая обработка', 'Работаем 7 дней в неделю', 'Гибкий график']
    },
    {
      icon: Award,
      title: 'Сертифицированные материалы',
      description: 'Используем только качественные материалы ведущих производителей: Dinitrol, WURTH.',
      features: ['Сертифицированные составы', 'Европейское качество', 'Долговечная защита']
    },
    {
      icon: Users,
      title: 'Опытные мастера',
      description: 'Наша команда состоит из профессионалов с опытом работы в сфере антикоррозийной защиты.',
      features: ['Профессиональные навыки', 'Постоянное обучение', 'Индивидуальный подход']
    },
    {
      icon: Zap,
      title: 'Современные технологии',
      description: 'Применяем передовые технологии и оборудование для достижения максимального качества обработки.',
      features: ['Новейшее оборудование', 'Инновационные методы', 'Контроль качества']
    },
    {
      icon: CheckCircle,
      title: 'Полный сервис',
      description: 'Предоставляем комплексные услуги: от диагностики до финальной обработки и последующего обслуживания.',
      features: ['Комплексный подход', 'Диагностика включена', 'Последующее обслуживание']
    }
  ]

  const stats = WHY_CHOOSE_US_STATS.map(stat => ({
    number: stat.value,
    label: stat.label
  }))

  return (
    <section className="section-padding bg-gradient-to-br from-secondary-50 to-primary-50">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            Почему выбирают именно нас
          </h2>
          <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
            Мы не просто выполняем работу - мы создаем долговечную защиту для вашего автомобиля,
            используя лучшие материалы и технологии
          </p>
        </motion.div>

        {/* Advantages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {advantages.map((advantage, index) => (
            <motion.div
              key={advantage.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <div className="card h-full hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200">
                  <advantage.icon className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                  {advantage.title}
                </h3>
                <p className="text-secondary-600 mb-4 text-sm leading-relaxed">
                  {advantage.description}
                </p>

                {/* Features */}
                <ul className="space-y-2">
                  {advantage.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-secondary-600">
                      <CheckCircle className="w-4 h-4 text-primary-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-2xl p-8 md:p-12 shadow-lg"
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
              Наши достижения в цифрах
            </h3>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              За годы работы мы помогли тысячам автовладельцев защитить их автомобили от коррозии
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  {stat.number}
                </div>
                <p className="text-secondary-600 text-sm md:text-base">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Quality Assurance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Гарантируем качество каждой работы
            </h3>
            <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
              Мы настолько уверены в качестве наших услуг, что предоставляем
              расширенную гарантию на все виды работ
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default WhyChooseUs
